import { useEffect, useState } from "react";
import { Card, ErrorBanner, Tile, fmt } from "../components/ui";
import { api, type DayOffsetRow, type HistoryDay, type LiveStats } from "../lib/api";
import { Performance } from "./Performance";

/** A bar chart drawn with divs. A charting library is 60kB for something
 *  four lines of CSS does, and this scales and themes for free. */
function Bars({
  rows,
  label,
  value,
  suffix = "",
  highlight,
}: {
  rows: Array<Record<string, unknown>>;
  label: (r: Record<string, unknown>) => string;
  value: (r: Record<string, unknown>) => number;
  suffix?: string;
  highlight?: (r: Record<string, unknown>) => boolean;
}) {
  const max = Math.max(1, ...rows.map(value));
  return (
    <div className="col" style={{ gap: 6 }}>
      {rows.map((r, i) => {
        const v = value(r);
        const hot = highlight?.(r);
        return (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "110px minmax(0,1fr) 74px",
              gap: 11,
              alignItems: "center",
              fontSize: 12.5,
            }}
          >
            <span className={hot ? "dim" : ""}>{label(r)}</span>
            <span
              style={{
                height: 20,
                borderRadius: 5,
                background: "var(--surf3)",
                overflow: "hidden",
              }}
            >
              <span
                style={{
                  display: "block",
                  height: "100%",
                  width: `${(v / max) * 100}%`,
                  background: hot ? "var(--surf3)" : "var(--acc)",
                  border: hot ? "1px dashed var(--ink3)" : undefined,
                }}
              />
            </span>
            <span className="mono" style={{ textAlign: "right", fontWeight: 620 }}>
              {v.toLocaleString("en-US")}
              {suffix}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function Analytics() {
  const [live, setLive] = useState<LiveStats | null>(null);
  const [history, setHistory] = useState<HistoryDay[]>([]);
  const [byDay, setByDay] = useState<DayOffsetRow[]>([]);
  // The curve is one campaign's. Day 3 of the Waterfall and day 3 of No CC
  // used to be added together, which described neither.
  const [dayCampaign, setDayCampaign] = useState<string>("");
  const [warehouse, setWarehouse] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.liveStats().then(setLive).catch((e) => setError(e.message));
    api.history(14).then((r) => {
      setHistory(r.days);
      if (!r.available) setWarehouse(r.reason ?? "no data yet");
    });
    api.byDayOffset().then((r) => setByDay(r.rows));
  }, []);

  const dayCampaigns = Array.from(
    byDay.reduce((m, r) => {
      const name = r.campaign ?? "All campaigns";
      return m.set(name, (m.get(name) ?? 0) + r.sent);
    }, new Map<string, number>()),
  ).sort((a, b) => b[1] - a[1]);
  const shownCampaign = dayCampaign || dayCampaigns[0]?.[0] || "";
  const dayRows = byDay
    .filter((r) => (r.campaign ?? "All campaigns") === shownCampaign)
    .sort((a, b) => a.day_offset - b.day_offset);

  const totals = history.reduce(
    (a, d) => ({
      sent: a.sent + d.sent,
      replies: a.replies + d.replies,
      opt_outs: a.opt_outs + d.opt_outs,
    }),
    { sent: 0, replies: 0, opt_outs: 0 },
  );

  return (
    <>
      <ErrorBanner error={error} />

      <div className="row w">
        <div>
          <h2 style={{ fontSize: 19 }}>Analytics</h2>
          <span className="lab">last 14 days · eastern time · replies counted as people</span>
        </div>
      </div>

      <div className="tiles">
        <Tile label="Sent" value={fmt.n(totals.sent)} detail="last 14 days" />
        <Tile
          label="Replies"
          value={fmt.n(totals.replies)}
          // Replies that arrived over messages sent, across the same fortnight.
          // Not a per-message rate: one message can start a conversation of
          // several, and a reply counts on the day it lands, so the two sides
          // are different populations and this is not bounded by 100%.
          detail={
            totals.sent
              ? `${fmt.pct((100 * totals.replies) / totals.sent)} of messages sent`
              : "—"
          }
          tone="ok"
        />
        <Tile
          label="Opt-outs"
          value={fmt.n(totals.opt_outs)}
          detail={
            totals.sent
              ? `${fmt.pct((100 * totals.opt_outs) / totals.sent)} of messages sent`
              : "—"
          }
          tone={totals.sent && totals.opt_outs / totals.sent > 0.01 ? "ba" : undefined}
        />
        <Tile label="In a campaign" value={fmt.n(live?.in_campaigns)} detail="right now" />
      </div>

      {warehouse && (
        <div className="banner warn">
          <b>Nothing in the warehouse yet.</b>
          <span>
            Charts fill in once messages start going out. Events reach BigQuery within a
            few minutes of being recorded.
          </span>
        </div>
      )}

      <div className="g2">
        <Card
          title="Sent and replies by day"
          footer={<span className="dim">Updated every few minutes, not live.</span>}
        >
          {history.length ? (
            <Bars
              rows={history as unknown as Array<Record<string, unknown>>}
              label={(r) => String(r.date).slice(5)}
              value={(r) => Number(r.sent)}
            />
          ) : (
            <div className="empty">No history yet.</div>
          )}
        </Card>

        <Card
          title="How deep the waterfall is worth going"
          footer={
            <span className="dim">
              Reply rate by campaign day. Where this flattens is where the drip stops
              earning its cost.
            </span>
          }
        >
          {dayCampaigns.length > 1 && (
            <select
              value={shownCampaign}
              onChange={(e) => setDayCampaign(e.target.value)}
              style={{ marginBottom: 10 }}
            >
              {dayCampaigns.map(([name]) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          )}
          {dayRows.length ? (
            <Bars
              rows={dayRows as unknown as Array<Record<string, unknown>>}
              label={(r) => `Day ${r.day_offset}`}
              value={(r) => Number(r.reply_rate)}
              suffix="%"
              highlight={(r) => Number(r.reply_rate) < 2}
            />
          ) : (
            <div className="empty">Not enough sends yet to show a curve.</div>
          )}
        </Card>
      </div>

      <Card
        title="Day by day"
        footer={
          <span className="dim">
            Reply rate here is the replies that arrived on a day over the messages
            sent on that day. They are not the same people: a reply to Tuesday's
            message counts on the day it lands, and one message can start a
            conversation of several, so this can read over 100% and is a daily
            volume check rather than a per-message rate. The per-message figure is
            the campaign-day chart above.
          </span>
        }
      >
        {history.length === 0 ? (
          <div className="empty">Nothing recorded yet.</div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Sent</th>
                  <th>Delivered</th>
                  <th>Replies</th>
                  <th>Reply rate</th>
                  <th>Opt-outs</th>
                </tr>
              </thead>
              <tbody>
                {[...history].reverse().map((d) => (
                  <tr key={d.date}>
                    <td className="mono">{d.date}</td>
                    <td className="mono">{fmt.n(d.sent)}</td>
                    <td className="mono">{fmt.n(d.delivered)}</td>
                    <td className="mono">{fmt.n(d.replies)}</td>
                    <td className="mono">
                      {d.sent ? fmt.pct((100 * d.replies) / d.sent) : "—"}
                    </td>
                    <td
                      className="mono"
                      style={{
                        color:
                          d.sent && d.opt_outs / d.sent > 0.01 ? "var(--bad)" : undefined,
                      }}
                    >
                      {fmt.n(d.opt_outs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Performance />
    </>
  );
}
